import {
  startRequest,
  endRequest,
  openDialog,
  closeDialog,
  openModal,
  closeModal,
  checkAccess,
  getParam,
  getAllGeneralPurposeMap,
  setContactId,
  setReferenceMode,
  setGmnWarning,
  setInitValues,
  setLoginTime,
} from './actions';
import {format} from 'date-fns';
import {getUserInfo} from '../auth/actions';

import axios, {HttpStatusCode} from 'axios';
import conf from './../../config/config.js';
import {API_CODE} from '../../common/common';
import {postRequest} from '../../common/backend.js';

/** ダイアログ種別 */
const DIALOG_TYPES = {
  info: 'I',
  warn: 'W',
  error: 'E',
  confirm: 'C',
  systemerror: 'S',
};

/**
 * メッセージ種別ごとのダイアログボタン情報。
 * @type {[type: string]: 'ok'|'okcancel'}
 */
const BUTTON_TYPES = {
  /** 情報 */
  I: 'ok',

  /** 警告 */
  W: 'okcancel',

  /** エラー */
  E: 'ok',

  /** 確認 */
  C: 'okcancel',

  /** システムエラー */
  S: 'error',
};

/**
 * @typedef MessageInfo メッセージ情報
 * @property {string} id メッセージID。'message.'部分は不要
 * @property {string[]} [values] メッセージ可変部分
 */

/**
 * @typedef DialogMessageInfo ダイアログメッセージ情報
 * @property {string|MessageInfo|(string|MessageInfo)[]} message メッセージ情報。文字列の場合メッセージID
 * @property {Function} [action] OKボタン押下時の処理
 * @property {Function} [cancelAction] キャンセルボタン押下時の処理
 * @property {'info'|'warn'|'error'|'confirm'|'systemerror'} [type] ダイアログ種別。
 * @property {boolean} [highPriority] 高優先度フラグ。
 */

/**
 * ダイアログ表示。
 * @param {DialogMessageInfo} {message, action, cancelAction, type}
 * @return {void}
 */
const doShowMessage = ({message, action, cancelAction, type, highPriority}) => {
  // メッセージの型をMessageInfo[]に統一する
  const messages = createMessages(message);

  let messageType;
  if (type == null) {
    // ダイアログ種別が指定されていない場合、メッセージ末尾 1文字目から種別を取得
    const idLength = messages[0].id.length;
    messageType = messages[0].id.substring(idLength - 1, idLength);
  } else {
    // ダイアログ種別が指定されている場合は定義から種別を取得
    messageType = DIALOG_TYPES[type];
  }

  // メッセージIDにプレフィックスを追加
  setPrefix(messages);

  let dialog = {
    type: BUTTON_TYPES[messageType],
    title: 'dialog.title.' + messageType,
    context: messages,
    action: action,
    cancelAction: cancelAction,
    open: true,
    address: window.location.pathname,
    highPriority: highPriority,
  };

  return (dispatch, getState) => {
    const {common} = getState();

    // 登録済みダイアログ情報を取得
    const beforeDialog = common.dialogInfo;

    // 前回メッセージなし/今回高優先度/今回低優先度かつ前回低優先度 の場合はダイアログ情報を上書き
    if (!beforeDialog || highPriority || !beforeDialog.highPriority) {
      dispatch(openDialog(dialog));
    }
  };
};

/**
 * メッセージの型変換。
 * メッセージをMessageInfo[]に変換して返却する。
 *
 * @param {string|MessageInfo|(string|MessageInfo)[]} message メッセージ情報
 * @return {MessageInfo[]} 変換後のメッセージ情報
 */
function createMessages(message) {
  // 配列の場合
  if (Array.isArray(message)) {
    const ret = [];
    for (const item of message) {
      if (typeof item === 'string') {
        // stringの場合はの場合はMessageInfo化
        ret.push({id: item});
      } else {
        // MessageInfoの場合はコピーして設定
        // メッセージを上書きしてしまうため。
        let copyItem = {};
        Object.assign(copyItem, item);
        ret.push(copyItem);
      }
    }

    return ret;
  }

  // string型の場合はMessageInfo[]化して返却
  if (typeof message === 'string') {
    return [{id: message}];
  }

  // object型の場合(MessageInfo)は配列化して返却
  return [message];
}

/**
 * プレフィックスの付加。
 * メッセージIDの先頭にプレフィックス"message."を付加する
 *
 * @param {MessageInfo[]} messages メッセージ一覧
 */
function setPrefix(messages) {
  for (const item of messages) {
    item.id = 'message.' + item.id;
  }
}

const modalInfo = {
  btnExist: false,
  open: false,
};

const doOpenModal = (btnExist) => {
  modalInfo.btnExist = btnExist;
  modalInfo.open = true;
  return (dispatch) => {
    dispatch(openModal(modalInfo));
  };
};

const doCloseModal = () => {
  return (dispatch) => {
    dispatch(closeModal());
  };
};

const accessInfo = {
  time: null,
};

const doCheckAccess = () => {
  // UTC変換したタイムスタンプを保持
  accessInfo.time = (
    new Date(Date.now() + new Date().getTimezoneOffset() * 60000)).getTime();
  return (dispatch) => {
    dispatch(checkAccess(accessInfo));
  };
};

/**
 * GETパラメータを解析して保持
 * @param {string} search props.location.searchを指定
 * @return {function}
 */
const doParseGetParam = (search) => {
  return (dispatch) => {
    const qs = new URLSearchParams(search);
    dispatch(getParam(qs));
    return qs;
  };
};

/**
 * 保持しているGETパラメータを破棄
 * @return {function}
 */
const doClearGetParam = () => {
  return (dispatch) => {
    dispatch(getParam(null));
  };
};

/**
 * ログインユーザ情報の取得.
 *
 * @return {Promise<object>} ログインユーザ情報
 */
export const getCurrentUser = async () => {
  const response = await postRequest('common/get-user-info');

  if (response.status != HttpStatusCode.Ok) {
    throw new Error(response);
  }

  return response.data;
};

/**
 * 汎用マスタを取得する
 * @return {Promise<object>} 検索結果
 */
const getGenericMst = async () => {
  const body = {};
  const response = await postRequest('common/get-generic-mst', body);
  if (response.status != HttpStatusCode.Ok) {
    throw new Error(response);
  }
  return response.data;
};

/**
 * ログイン時必要情報の一括取得処理。
 * @return {function}
 */
const doGetLoginInfo = () => {
  return async (dispatch) => {
    try {
      const [userInfo, mstResult] =
       await Promise.all([getCurrentUser(), getGenericMst()]);

      // ユーザ情報をストアへ登録
      dispatch(getUserInfo(userInfo));

      // 汎用マスタをストアへ登録
      const map = _createGeneralPurposeMap(mstResult.genericMst);
      dispatch(getAllGeneralPurposeMap(map));

      const loginTime = format(Date.now(), 'yyyy/MM/dd HH:mm');
      dispatch(setLoginTime(loginTime));

      return true;
    } catch (e) {
      return false;
    }
  };
};

/*
 * 取得した汎用マスタ一覧からマップを生成
 * @param {object[]} data 汎用マスタ一覧
 * @return {object} 汎用マスタマップ
 */
const _createGeneralPurposeMap = (data) => {
  const list = data ? data : [];

  const map = {};
  for (const data of list) {
    if (!map.hasOwnProperty(data.categoryType)) {
      map[data.categoryType] = [];
    }

    map[data.categoryType].push(data);
  }

  return map;
};

/**
 * 担当者マスタ ID設定
 * @param {number} id 担当者マスタID
 * @return {Promise<void>} 実行結果
 */
const doSetContactId = (id) => {
  console.log('担当者マスタID 設定...');

  return async (dispatch) => {
    dispatch(setContactId(id));
  };
};

/**
 * @typedef S3CreateInfo S3署名付きURL発行用情報
 * @property {string} id アップロード情報を一意に表すためのID
 * @property {string} contentType Content-Type
 * @property {string} fileName 拡張子付きファイル名
 * @property {string} subdir アップロードサブディレクトリ名
 */

/**
 * @typedef S3UploadInfo S3アップロード情報
 * @property {UploadFileInfo} uploadFile アップロードファイル情報
 * @property {S3CreateInfo} s3CreateInfo S3署名付きURL発行用情報
 */

/**
 * @typedef SignedUrlInfo ファイル登録用署名付きURL情報
 * @property {string} id アップロードファイルを判別するためのユニークなキー
 * @property {string} urls 署名付きURL
 */

/**
 * @typedef SignedUrlResult ファイル登録用署名付きURL情報
 * @property {string} uuid UUID(ファイルアップロードディレクトリ名)
 * @property {Object.<string, string>} urls ファイルIDと署名付きURLのマップ
 */

/**
 * ファイル S3アップロード
 * @param {Object.<string, UploadFileInfo[]>} files アップロードするファイル情報の一覧
 * @return {Promise<object>} ファイルアップロード結果
 */
const doUploadToS3 = (files) => {
  console.log('ファイル S3アップロード...');
  return async (dispatch, getState) => {
    return await doUploadToS3Common(files, getState, dispatch);
  };
};

/**
 * ファイル S3アップロード処理。(Operationから呼ぶ処理)
 * @param {Object.<string, UploadFileInfo[]>} files アップロードするファイル情報の一覧
 * @param {function} getState State取得処理
 * @param {function} dispatch ディスパッチ処理
 * @return {Promise<object>} ファイルアップロード結果
 */
const doUploadToS3Common = async (files, getState, dispatch) => {
  const {auth} = getState();
  const token = auth.loginInfo.idToken;

  let result = null;
  try {
    // S3アップロード用の情報生成
    const uploadFileInfo = _createUploadInfo(files);

    // 署名付きURLの発行
    const signedUrlInfo = await _getS3SiengedUrls(uploadFileInfo, token);

    // 署名付きURLを使用してファイルアップロード
    const responses =
      await _putToS3SignedUrl(uploadFileInfo, signedUrlInfo, dispatch);

    // アップロードレスポンスのチェック
    result = _checkS3UploadResult(responses, uploadFileInfo, signedUrlInfo);
  } catch (err) {
    console.error(err);
    return null;
  }

  return result;
};

/**
 * S3アップロード情報の生成
 * @private
 * @param {Object.<string, UploadFileInfo|UploadFileInfo[]>} uploadFiles アップロードするファイル情報の一覧
 * @return {Object.<string, S3UploadInfo[]>} S3アップロード情報
 */
const _createUploadInfo = (uploadFiles) => {
  /** @type Object.<string, S3UploadInfo[]> */
  const uploadFileInfo = {};

  for (const componentId of Object.keys(uploadFiles)) {
    let files = uploadFiles[componentId];

    if (!Array.isArray(files)) {
      files = [files];
    }

    for (const file of files) {
      // ContentDocumentId設定済の場合、アップロード済なので除外
      if (file.ContentDocumentId != null) {
        continue;
      }

      const id = [componentId, file.FileName].join('-');
      const s3createInfo = {
        id: id,
        contentType: file.ContentType,
        fileName: file.FileName,
        subdir: componentId,
      };

      uploadFileInfo[id] = {
        uploadFile: file,
        s3CreateInfo: s3createInfo,
      };
    }
  }

  return uploadFileInfo;
};

/**
 * 署名付きURLの取得。
 * @private
 * @param {Object.<string, S3UploadInfo[]>} uploadFileInfo S3アップロード情報
 * @param {string} token API実行用セキュリティトークン
 * @return {Promise<SignedUrlResult>} 署名付きURL情報。
 */
const _getS3SiengedUrls = async (uploadFileInfo, token) => {
  // S3アップロード情報から、署名付きURL発行用情報作成
  const uploadFiles = [];
  for (const id of Object.keys(uploadFileInfo)) {
    uploadFiles.push(uploadFileInfo[id].s3CreateInfo);
  }

  // 署名付きURL発行APIを実行
  const ret = await axios.post(conf.API_URL + 'File/getSignedUrl',
      {fileInfo: uploadFiles},
      {headers: {Authorization: token}});
  const body = ret.data.body;

  // エラーの場合
  if (body.errorCode != '00000') {
    throw new Error('API getSignedUrl 実行エラー');
  }

  return body.data;
};

/**
 * S3アップロード。
 * @private
 * @param {Object.<string, S3UploadInfo[]>} uploadFileInfo S3アップロード情報
 * @param {SignedUrlResult} signedUrlInfo 署名付きURL情報
 * @param {function} dispatch
 * @return {Promise<AxiosResponse[]>} S3アップロード結果
 */
const _putToS3SignedUrl = async (uploadFileInfo, signedUrlInfo, dispatch) => {
  const api = axios.create();
  dispatch(startRequest());

  // 署名付きURLでファイルのアップロードを行う
  /** @type import('axios').AxiosPromise<any>[] */
  const uploadPromises = [];
  for (const id of Object.keys(uploadFileInfo)) {
    const fileInfo = uploadFileInfo[id].uploadFile;

    const upInfo = {
      method: 'PUT',
      url: signedUrlInfo.urls[id],
      headers: {'Content-Type': fileInfo.ContentType},
      data: fileInfo.FileData,
    };

    uploadPromises.push(api(upInfo));
  }

  try {
    // 全てのアップロードが完了したら処理終了する
    const result = await axios.all(uploadPromises);
    return result;
  } finally {
    dispatch(endRequest());
  }
};

/**
 * S3アップロード結果のチェック。
 * @private
 * @param {AxiosResponse[]} uploadResponses アップロード結果一覧
 * @param {Object.<string, S3UploadInfo[]>} uploadFileInfo S3アップロード情報
 * @param {SignedUrlResult} signedUrlInfo 署名付きURL情報
 * @return {object} アップロード結果情報
 */
const _checkS3UploadResult = (
    uploadResponses, uploadFileInfo, signedUrlInfo) => {
  let success = true;
  const uploadFiles = [];
  const errorFiles = [];

  for (const response of uploadResponses) {
    const fileId = _getFileId(response, signedUrlInfo);
    if (fileId == null || !uploadFileInfo[fileId]) {
      // IDやファイル情報が取得できない場合はスキップ(本来ありえない)
      console.warn('情報不一致 File Id:' + fileId);
      continue;
    }

    const s3Info = uploadFileInfo[fileId].s3CreateInfo;

    // ファイル登録失敗のレスポンスがある場合は失敗
    if (response.statusText !== 'OK' && response.status !== 200) {
      success = false;
      errorFiles.push(s3Info);
      continue;
    }

    // アップロードしたファイルの情報を設定
    uploadFiles.push(s3Info);
  }

  const uuid = signedUrlInfo.uuid;

  return {
    success,
    uuid,
    uploadFiles,
    errorFiles,
  };
};

/**
 * ファイルアップロードチェックエラー時のAPI実行結果置き換え。
 *
 * S3ファイルアップロード(doUploadToS3Common)の結果をチェックし、
 * エラーの場合、かつAPI実行結果は成功の場合、
 * API実行結果のエラーコードをアップロードエラーに置き換えます。
 * @see doUploadToS3Common
 * @param {object} s3UploadResult S3ファイルアップロード結果
 * @param {object} apiResult API実行結果
 */
export function doCheckS3UploadResult(s3UploadResult, apiResult) {
  if (!s3UploadResult ||
      !Object.hasOwn(s3UploadResult, 'uploadFiles')) {
    return;
  }
  if (s3UploadResult.uploadFiles &&
    s3UploadResult.uploadFiles.length == 0) {
    return;
  }
  const retBody = apiResult.data.body;
  if (!s3UploadResult.success &&
    retBody.errorCode == API_CODE.SUCCESS) {
    retBody.errorCode = API_CODE.ERROR_FILE_UPDATE;
  }
}

/**
 * アップロードしたファイルIDの判定。
 * @private
 * @param {AxiosResponse} response アップロード結果
 * @param {SignedUrlResult} signedUrlInfo 署名付きURL情報
 * @return {string} ファイルID。存在しない場合はnull
 */
const _getFileId = (response, signedUrlInfo) => {
  const checkUrls = signedUrlInfo.urls;
  const find = Object.keys(checkUrls).filter((key) => {
    return checkUrls[key] === response.config.url;
  });

  if (find.length > 0) {
    return find[0];
  } else {
    return null;
  }
};

/**
 * 画面の参照状態 設定
 *
 * @param {bool} referenceMode true: 参照, false: 参照以外
 * @return {void} 設定結果
 */
const doSetReferenceMode = (referenceMode) => {
  console.log('画面の参照状態 設定...');

  return async (dispatch) => {
    dispatch(setReferenceMode(referenceMode));
  };
};

/**
 * 警告設定
 * @param {string} Id 画面ID
 * @param {string} warning 警告有無
 * @return {Promise<void>} 登録結果
 */
const doSetGmnWarning = (Id, warning) => {
  console.log('画面警告 設定...');

  return async (dispatch) => {
    dispatch(setGmnWarning(Id, warning));
  };
};

/**
 * Field 初期値設定
 * @param {object} data 初期値
 * @return {Promise<void>} 結果
 */
const doSetInitValues = (data) => {
  return async (dispatch) => {
    dispatch(setInitValues(data));
  };
};

export default {
  startRequest,
  endRequest,
  doShowMessage,
  closeDialog,
  doOpenModal,
  doCloseModal,
  doCheckAccess,
  doParseGetParam,
  doClearGetParam,
  doGetLoginInfo,
  doSetContactId,
  doUploadToS3,
  doUploadToS3Common,
  doCheckS3UploadResult,
  doSetReferenceMode,
  doSetGmnWarning,
  doSetInitValues,
};
